
import React, { useState, useEffect, useRef } from 'react';
import { AppMode, Language, TRANSLATIONS, Theme } from './types';
import { StudyMode } from './components/StudyMode';
import { TestMode } from './components/TestMode';
import { CheckerMode } from './components/CheckerMode';
import { InfoMode } from './components/InfoMode';
import { CertificateMode } from './components/CertificateMode';
import { SignIn } from './components/SignIn';
import { AuthModal } from './components/AuthModal';
import { ContactModal } from './components/ContactModal';
import { AccountSettings } from './components/AccountSettings';
import { NeuralCore } from './components/NeuralCore';
import { ApiKeyScreen } from './components/ApiKeyScreen';
import { BillingScreen } from './components/BillingScreen';
import { TermsScreen } from './components/TermsScreen';
import { Globe, ChevronDown, UserPlus, LogIn, Clock, FileText, Layers, RefreshCcw, X } from 'lucide-react';
import { Loader } from './components/Loader';
import { Reveal } from './components/Reveal';
import { checkAuthUserByEmail, supabase, upsertUser } from './services/supabaseClient';
import faviconIcon from './files/favicon.svg';

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
const supabaseFunctionsAnonKey = (import.meta.env.VITE_SUPABASE_FUNCTIONS_ANON_KEY as string | undefined) ?? supabaseAnonKey;

const decodeJwtPayload = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const homeVideoSrc = '/files/AAADemo.mp4';
const figmaLogo = new URL('./files/figma-logo.svg', import.meta.url).href;
const unsplashGreenFlow = new URL('./files/unsplash-green-flow.jpg', import.meta.url).href;
const pluginFigmaAsset = new URL('./files/PluginFigma.png', import.meta.url).href;
const workflowImage = unsplashGreenFlow;
const pluginImage = pluginFigmaAsset;
const PLAN_CREDITS: Record<number, number> = {
  1: 5,
  2: 25,
  3: 100,
  4: 200,
};

const ANALYSIS_QUERY_KEY = 'analysis';
const ANALYSIS_STORAGE_PREFIX = 'aaaquest_checker_analysis_';

const getPlanLabel = (planId: number | null | undefined, t: any) => {
  if (planId === 2) return t.pricingStarterName;
  if (planId === 3) return t.pricingProName;
  if (planId === 4) return t.pricingEnterpriseName;
  return t.freePlanName;
};

const resolveBrowserLanguage = (): Language => {
  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const normalized = String(candidate).toLowerCase();
    if (
      normalized.startsWith('es')
      || normalized.startsWith('eu')
      || normalized.startsWith('ca')
      || normalized.startsWith('gl')
    ) {
      return 'es';
    }
    if (normalized.startsWith('en')) {
      return 'en';
    }
  }

  return 'en';
};

const CreditsBadge: React.FC<{
  theme: Theme;
  t: any;
  planId?: number | null;
  creditsRemaining?: number | null;
  onClick?: () => void;
}> = ({
  theme,
  t,
  planId,
  creditsRemaining,
  onClick,
}) => {
  const resolvedPlan = [1, 2, 3, 4].includes(Number(planId)) ? Number(planId) : 1;
  const planTotal = PLAN_CREDITS[resolvedPlan] ?? PLAN_CREDITS[1];
  const resolvedRemaining =
    typeof creditsRemaining === 'number' && Number.isFinite(creditsRemaining)
      ? Math.max(0, Math.min(creditsRemaining, planTotal))
      : planTotal;
  const progress = Math.round((resolvedRemaining / planTotal) * 100);
  const planLabel = getPlanLabel(resolvedPlan, t);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-full px-3 h-10 border ${
        theme === 'dark'
          ? 'border-white/10 bg-slate-900/70 text-white'
          : 'border-slate-200 bg-white text-slate-900'
      } transition-colors ${theme === 'dark' ? 'hover:bg-slate-800/80' : 'hover:bg-slate-50'}`}
      aria-label={`${planLabel}. ${resolvedRemaining} ${t.creditsAvailableLabel}`}
    >
      <div className="relative h-7 w-7">
        <svg viewBox="0 0 36 36" className="h-7 w-7">
          <path
            d="M18 2.0845
               a 15.9155 15.9155 0 0 1 0 31.831
               a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}
            strokeWidth="4"
          />
          <path
            d="M18 2.0845
               a 15.9155 15.9155 0 0 1 0 31.831
               a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#10B981"
            strokeWidth="4"
            strokeDasharray={`${progress}, 100`}
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="flex min-w-[118px] flex-col items-start leading-tight text-left">
        <div className="w-full text-left text-xs font-semibold">{planLabel}</div>
        <div className={`w-full text-left text-[11px] ${theme === 'dark' ? 'text-emerald-300' : 'text-emerald-600'}`}>
          {resolvedRemaining} {t.creditsAvailableLabel}
        </div>
      </div>
    </button>
  );
};

const CreditsBadgeSkeleton: React.FC<{ theme: Theme }> = ({ theme }) => (
  <div
    className={`flex items-center gap-3 rounded-full px-3 h-10 border animate-pulse ${
      theme === 'dark'
        ? 'border-white/10 bg-slate-900/70'
        : 'border-slate-200 bg-white'
    }`}
    aria-hidden="true"
  >
    <div className={`h-7 w-7 rounded-full ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
    <div className="flex min-w-[118px] flex-col gap-1">
      <div className={`h-2.5 w-12 rounded ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
      <div className={`h-2.5 w-24 rounded ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
    </div>
  </div>
);

const App: React.FC = () => {
  const basePath = import.meta.env.BASE_URL || '/';
  const normalizePath = (value: string) => value.replace(/\/+$/, '') || '/';
  const normalizedHome = normalizePath(basePath);
  const homeHref = basePath.endsWith('/') ? basePath : `${basePath}/`;
  const appHref = normalizedHome === '/' ? '/app' : `${normalizedHome}/app`;
  const accountHref = `${appHref}/mi-cuenta`;
  const planHref = `${appHref}/plan`;
  const apiKeyHref = `${appHref}/api-key`;
  const billingHref = `${appHref}/billing`;
  const termsHref = `${appHref}/terms`;

  const resolveModeFromPath = () => {
    const path = normalizePath(window.location.pathname);
    if (path === normalizePath(apiKeyHref)) {
      return AppMode.API_KEY;
    }
    if (path === normalizePath(billingHref)) {
      return AppMode.BILLING;
    }
    if (path === normalizePath(termsHref)) {
      return AppMode.TERMS;
    }
    if (path === normalizePath(planHref)) {
      return AppMode.PLAN;
    }
    if (path === normalizePath(accountHref)) {
      return AppMode.ACCOUNT;
    }
    if (path === normalizePath(appHref)) {
      return AppMode.CHECKER;
    }
    return AppMode.HOME;
  };

  const [mode, setMode] = useState<AppMode>(() => resolveModeFromPath());
  const [focusMode, setFocusMode] = useState(false);
  const LANGUAGE_STORAGE_KEY = 'aaaquest_language';
  const getInitialLanguage = (): Language => {
    try {
      const params = new URLSearchParams(window.location.search);
      const activeAnalysisId = params.get(ANALYSIS_QUERY_KEY);
      if (activeAnalysisId) {
        const rawAnalysis = sessionStorage.getItem(`${ANALYSIS_STORAGE_PREFIX}${activeAnalysisId}`);
        if (rawAnalysis) {
          const parsed = JSON.parse(rawAnalysis) as { language?: Language };
          if (parsed.language === 'es' || parsed.language === 'en') {
            return parsed.language;
          }
        }
      }
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === 'es' || stored === 'en') return stored;
    } catch {
      // ignore storage/parser errors
    }
    return resolveBrowserLanguage();
  };
  const [language, setLanguage] = useState<Language>(() => getInitialLanguage());
  const theme: Theme = 'dark'; // Default to dark mode
  const [isLoadingLanguage, setIsLoadingLanguage] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authEntry, setAuthEntry] = useState<'signup' | 'signin'>('signup');
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState<number | null>(null);
  const [isCheckoutRedirecting, setIsCheckoutRedirecting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const toastCloseRef = useRef<HTMLButtonElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const maintenanceDialogRef = useRef<HTMLDivElement | null>(null);
  const [authUser, setAuthUser] = useState<any | null>(null);
  const [authName, setAuthName] = useState<string>('');
  const [userPlanId, setUserPlanId] = useState<number | null>(null);
  const [userCreditsRemaining, setUserCreditsRemaining] = useState<number | null>(null);
  const [isHeaderEntitlementsLoading, setIsHeaderEntitlementsLoading] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenanceBody, setMaintenanceBody] = useState<string | null>(null);
  const [showMaintenanceLink, setShowMaintenanceLink] = useState(true);
  const [showPricingContactModal, setShowPricingContactModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookiePrefs, setShowCookiePrefs] = useState(false);
  const [cookieAnalytics, setCookieAnalytics] = useState(true);
  const [cookieMarketing, setCookieMarketing] = useState(true);
  const [allowLandingAccess, setAllowLandingAccess] = useState(false);

  const isPublicMode = (nextMode: AppMode) =>
    [
      AppMode.HOME,
      AppMode.STUDY,
      AppMode.TEST,
      AppMode.INFO,
      AppMode.CERTIFICATE,
      AppMode.SIGNIN,
    ].includes(nextMode);

  const navigateMode = (nextMode: AppMode) => {
    const isLockedToApp = Boolean(authUser) && mode !== AppMode.HOME;
    const resolvedMode = isLockedToApp && isPublicMode(nextMode) ? AppMode.CHECKER : nextMode;
    setMode(resolvedMode);
    if (resolvedMode === AppMode.CHECKER) {
      history.pushState(null, '', appHref);
    } else if (resolvedMode === AppMode.ACCOUNT) {
      history.pushState(null, '', accountHref);
    } else if (resolvedMode === AppMode.API_KEY) {
      history.pushState(null, '', apiKeyHref);
    } else if (resolvedMode === AppMode.BILLING) {
      history.pushState(null, '', billingHref);
    } else if (resolvedMode === AppMode.TERMS) {
      history.pushState(null, '', termsHref);
    } else if (resolvedMode === AppMode.PLAN) {
      history.pushState(null, '', planHref);
    } else if (resolvedMode === AppMode.HOME) {
      history.pushState(null, '', homeHref);
    }
  };

  const handleHeaderLogoClick = () => {
    if (mode === AppMode.CHECKER) {
      window.dispatchEvent(new CustomEvent('aaaquest:checker-reset'));
      history.pushState(null, '', appHref);
      setMode(AppMode.CHECKER);
      return;
    }
    navigateMode(AppMode.CHECKER);
  };

  // Scroll to top whenever mode changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [mode]);

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (userMenuRef.current?.contains(target) || userMenuButtonRef.current?.contains(target)) {
        return;
      }
      setIsUserMenuOpen(false);
    };
    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [isUserMenuOpen]);

  useEffect(() => {
    const stored = document.cookie.split('; ').find((row) => row.startsWith('cookie_prefs='));
    if (!stored) {
      setShowCookieBanner(true);
      return;
    }
    const value = stored.split('=')[1] ?? '';
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      if (typeof parsed?.analytics === 'boolean') setCookieAnalytics(parsed.analytics);
      if (typeof parsed?.marketing === 'boolean') setCookieMarketing(parsed.marketing);
      setShowCookieBanner(false);
    } catch {
      setShowCookieBanner(true);
    }
  }, []);

  const persistCookiePrefs = (prefs: { analytics: boolean; marketing: boolean }) => {
    const payload = encodeURIComponent(JSON.stringify({ essential: true, ...prefs }));
    document.cookie = `cookie_prefs=${payload}; path=/; max-age=31536000; SameSite=Lax`;
  };

  useEffect(() => {
    const seen = sessionStorage.getItem('maintenanceSeen');
    if (!seen) {
      openMaintenanceModal();
      sessionStorage.setItem('maintenanceSeen', '1');
    }
  }, []);

  useEffect(() => {
    setMode(resolveModeFromPath());
    const path = normalizePath(window.location.pathname);
    const isLandingPath = path === normalizedHome;
    let isDirect = true;
    if (document.referrer) {
      try {
        isDirect = new URL(document.referrer).origin !== window.location.origin;
      } catch {
        isDirect = true;
      }
    }
    if (isLandingPath && isDirect) {
      setAllowLandingAccess(true);
    }
    const handlePopState = () => {
      setMode(resolveModeFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!isAuthOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isAuthOpen]);

  useEffect(() => {
    if (!showMaintenance) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dialog = maintenanceDialogRef.current;
    const focusableSelector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusables = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector)) : [];
    const focusFirst = () => {
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        dialog?.focus();
      }
    };
    const focusId = window.setTimeout(focusFirst, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowMaintenance(false);
        return;
      }

      if (event.key !== 'Tab' || focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(focusId);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMaintenance]);

  const t = TRANSLATIONS[language];
  const activeMaintenanceBody = maintenanceBody ?? t.maintenanceBody;

  const openMaintenanceModal = (body?: string, withLink = true) => {
    setMaintenanceBody(body ?? t.maintenanceBody);
    setShowMaintenanceLink(withLink);
    setShowMaintenance(true);
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!toastMessage) return;
    const id = window.setTimeout(() => {
      toastCloseRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [toastMessage]);

  const openAuth = async (entry: 'signup' | 'signin') => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      showToast(t.alreadyRegisteredToast);
      return;
    }
    setAuthEntry(entry);
    setIsAuthOpen(true);
  };

  const getWelcomeSeenKey = (userId: string) => `aaaquest_welcome_seen_${userId}`;

  const hasSeenWelcomeModal = (userId: string | null | undefined) => {
    if (!userId) return false;
    try {
      return localStorage.getItem(getWelcomeSeenKey(userId)) === '1';
    } catch {
      return false;
    }
  };

  const markWelcomeModalSeen = (userId: string | null | undefined) => {
    if (!userId) return;
    try {
      localStorage.setItem(getWelcomeSeenKey(userId), '1');
    } catch {
      // ignore storage errors
    }
  };

  const clearPendingCheckoutPlan = () => {
    setPendingCheckoutPlan(null);
    sessionStorage.removeItem('pendingCheckoutPlan');
    localStorage.removeItem('pendingCheckoutPlan');
    try {
      const current = window.name ? JSON.parse(window.name) : null;
      if (current && typeof current === 'object' && 'pendingCheckoutPlan' in current) {
        delete current.pendingCheckoutPlan;
        window.name = JSON.stringify(current);
      }
    } catch {
      // ignore parse errors
    }
  };

  const getStoredCheckoutPlan = () => {
    const fromSession = sessionStorage.getItem('pendingCheckoutPlan');
    const fromLocal = localStorage.getItem('pendingCheckoutPlan');
    let fromWindow: string | null = null;
    try {
      const current = window.name ? JSON.parse(window.name) : null;
      fromWindow = current?.pendingCheckoutPlan || null;
    } catch {
      fromWindow = null;
    }
    const raw = fromSession || fromLocal || fromWindow;
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const createCheckoutSession = async (planId: number) => {
    const traceId = `co_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const requestSiteUrl = window.location.origin;
    const returnPath = appHref;
    setIsCheckoutRedirecting(true);
    console.info('[checkout] start', { planId, traceId, requestSiteUrl, returnPath });
    const { data: sessionData } = await supabase.auth.getSession();
    let accessToken = sessionData.session?.access_token ?? null;
    const initialClaims = accessToken ? decodeJwtPayload(accessToken) : null;
    console.info('[checkout] getSession', {
      traceId,
      hasSession: Boolean(sessionData.session),
      hasAccessToken: Boolean(accessToken),
      tokenSub: sessionData.session?.user?.id ?? null,
      tokenRole: initialClaims?.role ?? null,
      tokenAud: initialClaims?.aud ?? null,
      tokenExp: initialClaims?.exp ?? null,
    });
    if (!accessToken) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      accessToken = refreshed.session?.access_token ?? null;
      const refreshedClaims = accessToken ? decodeJwtPayload(accessToken) : null;
      console.info('[checkout] refreshSession (initial)', {
        traceId,
        hasSession: Boolean(refreshed.session),
        hasAccessToken: Boolean(accessToken),
        tokenSub: refreshed.session?.user?.id ?? null,
        tokenRole: refreshedClaims?.role ?? null,
        tokenAud: refreshedClaims?.aud ?? null,
        tokenExp: refreshedClaims?.exp ?? null,
      });
    }
    if (!accessToken) {
      console.warn('[checkout] missing access token after refresh');
      setIsCheckoutRedirecting(false);
      openAuth('signin');
      return;
    }

    try {
      const authProbeRes = await fetch(`${supabaseUrl}/auth/v1/user`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: supabaseAnonKey,
        },
      });
      let authProbeBody: any = null;
      try {
        authProbeBody = await authProbeRes.json();
      } catch {
        authProbeBody = null;
      }
      console.info('[checkout] auth probe', {
        traceId,
        status: authProbeRes.status,
        ok: authProbeRes.ok,
        sub: authProbeBody?.id ?? null,
        error: authProbeBody?.msg ?? authProbeBody?.message ?? null,
        body: authProbeBody,
      });
    } catch (probeError) {
      console.error('[checkout] auth probe failed', { traceId, probeError });
    }

    const invokeCheckout = async (token: string) => {
      const tokenClaims = decodeJwtPayload(token);
      console.info('[checkout] invoke request', {
        traceId,
        url: `${supabaseUrl}/functions/v1/create-checkout-session`,
        hasSupabaseUrl: Boolean(supabaseUrl),
        hasAnonKey: Boolean(supabaseFunctionsAnonKey),
        anonKeyType: supabaseFunctionsAnonKey.startsWith('eyJ')
          ? 'legacy_jwt'
          : supabaseFunctionsAnonKey.startsWith('sb_publishable_')
            ? 'publishable'
            : 'unknown',
        tokenLength: token.length,
        tokenPrefix: token.slice(0, 16),
        tokenSub: tokenClaims?.sub ?? null,
        tokenRole: tokenClaims?.role ?? null,
        tokenAud: tokenClaims?.aud ?? null,
        tokenExp: tokenClaims?.exp ?? null,
        requestSiteUrl,
        returnPath,
      });
      if (!supabaseUrl || !supabaseFunctionsAnonKey) {
        console.error('[checkout] missing env vars in frontend', {
          traceId,
          hasSupabaseUrl: Boolean(supabaseUrl),
          hasAnonKey: Boolean(supabaseFunctionsAnonKey),
        });
        return { data: null, error: { status: 500, message: 'Missing Supabase env vars' } };
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: supabaseFunctionsAnonKey,
          'Content-Type': 'application/json',
          'x-checkout-trace-id': traceId,
        },
        body: JSON.stringify({ planId, siteUrl: requestSiteUrl, returnPath }),
      });

      let data: any = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      console.info('[checkout] invoke response', {
        traceId,
        status: response.status,
        ok: response.ok,
        responseTraceId: response.headers.get('x-checkout-trace-id'),
        responseContentType: response.headers.get('content-type'),
        responseCfRay: response.headers.get('cf-ray'),
        responseDate: response.headers.get('date'),
        data,
      });

      if (!response.ok) {
        return {
          data: null,
          error: {
            status: response.status,
            message: data?.error ?? 'Checkout session failed',
            raw: data,
          },
        };
      }

      return { data, error: null };
    };

    let { data, error } = await invokeCheckout(accessToken);
    console.info('[checkout] first attempt result', { traceId, error, data });

    const status = (error as any)?.status ?? (error as any)?.context?.status;
    if (status === 401) {
      console.warn('[checkout] first attempt 401, refreshing token and retrying', { traceId });
      const { data: refreshed } = await supabase.auth.refreshSession();
      const refreshedToken = refreshed.session?.access_token ?? null;
      const refreshedClaims = refreshedToken ? decodeJwtPayload(refreshedToken) : null;
      console.info('[checkout] refreshSession (retry)', {
        traceId,
        hasSession: Boolean(refreshed.session),
        hasAccessToken: Boolean(refreshedToken),
        tokenSub: refreshed.session?.user?.id ?? null,
        tokenRole: refreshedClaims?.role ?? null,
        tokenAud: refreshedClaims?.aud ?? null,
        tokenExp: refreshedClaims?.exp ?? null,
      });
      if (!refreshedToken) {
        console.warn('[checkout] missing token on retry refresh', { traceId });
        setIsCheckoutRedirecting(false);
        openAuth('signin');
        return;
      }
      ({ data, error } = await invokeCheckout(refreshedToken));
      console.info('[checkout] second attempt result', { traceId, error, data });
    }

    if (error || !data?.url) {
      console.error('[checkout] create-checkout-session failed', { traceId, error, data });
      setIsCheckoutRedirecting(false);
      const checkoutErrorMessage = (error as any)?.message || (error as any)?.raw?.error || t.paymentSessionError || 'No pudimos abrir la pasarela de pago.';
      console.error('[checkout] create-checkout-session failed detail', {
        traceId,
        status: (error as any)?.status ?? null,
        message: checkoutErrorMessage,
        raw: (error as any)?.raw ?? null,
      });
      showToast(`${checkoutErrorMessage} · Ref ${traceId}`);
      return;
    }

    console.info('[checkout] redirect stripe', { traceId, url: data.url });
    window.location.href = data.url as string;
  };
  const handlePricingCta = (ctaHref?: string, planId?: number) => {
    if (!ctaHref) return;
    if (ctaHref === 'signup') {
      openAuth('signup');
      return;
    }
    if (ctaHref.startsWith('mailto:')) {
      window.location.href = ctaHref;
      setShowPricingContactModal(true);
      return;
    }
    openMaintenanceModal();
  };

  const openContact = () => {
    setIsContactOpen(true);
  };

  useEffect(() => {
    const exchangeCode = async () => {
      const params = new URLSearchParams(window.location.search);
      const hasCode = params.get('code');
      if (hasCode) {
        const oauthPending = sessionStorage.getItem('oauthPending') === '1';
        const { data: initialSession } = await supabase.auth.getSession();
        const hasSession = Boolean(initialSession.session?.access_token);

        console.info('[auth][oauth] code detected', {
          url: window.location.href,
          oauthPending,
          hasSession,
        });

        if (oauthPending && !hasSession) {
          try {
            const { data, error } = await supabase.auth.exchangeCodeForSession(window.location.href);
            if (error) {
              console.warn('[auth][oauth] exchange error', error);
            } else {
              console.info('[auth][oauth] exchange success', {
                userId: data?.session?.user?.id ?? null,
              });
            }
          } catch (error) {
            console.warn('Failed to exchange auth code', error);
          } finally {
            sessionStorage.removeItem('oauthPending');
          }
        } else {
          console.info('[auth][oauth] skipping exchange', {
            reason: oauthPending ? 'session_already_exists' : 'oauth_not_pending',
          });
        }

        const pendingPlan = getStoredCheckoutPlan();
        if (pendingPlan) {
          clearPendingCheckoutPlan();
          await createCheckoutSession(pendingPlan);
          return;
        }

        setAllowLandingAccess(false);
        setMode(AppMode.CHECKER);

        const cleanParams = new URLSearchParams(window.location.search);
        cleanParams.delete('code');
        cleanParams.delete('state');
        const cleanQuery = cleanParams.toString();
        const cleanUrl = `${appHref}${cleanQuery ? `?${cleanQuery}` : ''}`;
        history.replaceState(null, '', cleanUrl);
        return;
        }
    };

    exchangeCode();

    const handleAuthUser = async (user: any, shouldToast: boolean) => {
      console.info('[auth] handleAuthUser', {
        id: user?.id,
        email: user?.email,
        provider: user?.app_metadata?.provider,
      });
      const fallbackName =
        user.user_metadata?.full_name
        || user.user_metadata?.name
        || user.email?.split('@')[0]
        || '';
      setAuthUser(user);
      setAuthName(fallbackName);
      const createdAt = user?.created_at ?? '';
      const lastSignInAt = user?.last_sign_in_at ?? '';
      const isNewUser = Boolean(createdAt && lastSignInAt && createdAt === lastSignInAt);
      let name = '';
      try {
        if (user.email) {
          const check = await checkAuthUserByEmail(user.email);
          if (check?.user) {
            name =
              check.user.user_metadata?.full_name
              || check.user.user_metadata?.name
              || check.user.email?.split('@')[0]
              || '';
          }
        } else {
          // no email: keep fallback name
        }
      } catch (error) {
        console.warn('Auth lookup failed', error);
      }

      upsertUser(user).catch((error: any) => {
        console.warn('Failed to upsert user', error);
      });

      const resolvedName = name || fallbackName;
      if (resolvedName && resolvedName !== fallbackName) {
        setAuthName(resolvedName);
      }

      const storedPlan = pendingCheckoutPlan ?? getStoredCheckoutPlan();
      if (storedPlan) {
        clearPendingCheckoutPlan();
        await createCheckoutSession(storedPlan);
        return;
      }

      if (isNewUser && user?.id && !hasSeenWelcomeModal(user.id)) {
        markWelcomeModalSeen(user.id);
        setShowWelcomeModal(true);
      }

      if (shouldToast) {
        setAllowLandingAccess(false);
        navigateMode(AppMode.CHECKER);
        if (isNewUser) {
          const displayName =
            name || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || t.userFallbackName;
          showToast(`${t.signupSuccessPrefix}${displayName}`);
        }
      }

    };

    const shouldToastFromFlags = () => {
      return sessionStorage.getItem('signupToast') === '1';
    };

    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) {
        const shouldToast = shouldToastFromFlags();
        if (sessionStorage.getItem('oauthPending') === '1') sessionStorage.removeItem('oauthPending');
        handleAuthUser(data.session.user, shouldToast);
      } else {
        setAuthUser(null);
        setAuthName('');
        setShowWelcomeModal(false);
      }
      setAuthChecked(true);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.info('[auth] onAuthStateChange', {
        event,
        userId: session?.user?.id ?? null,
        provider: session?.user?.app_metadata?.provider ?? null,
      });
      if (session?.user) {
        const shouldToast = shouldToastFromFlags();
        if (sessionStorage.getItem('oauthPending') === '1') sessionStorage.removeItem('oauthPending');
        handleAuthUser(session.user, shouldToast);
      } else {
        setAuthUser(null);
        setAuthName('');
        setShowWelcomeModal(false);
      }
      if (!authChecked) setAuthChecked(true);
    });

    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') === '1' || sessionStorage.getItem('signupToast') === '1') {
      const signupName = sessionStorage.getItem('signupToastName');
      if (signupName) {
        showToast(`${t.signupSuccessPrefix}${signupName}`);
        sessionStorage.removeItem('signupToastName');
      } else {
        showToast(t.signupSuccessToast);
      }
      params.delete('signup');
      sessionStorage.removeItem('signupToast');
      const query = params.toString();
      const cleanHash = window.location.hash
        .replace(/access_token=[^&]+&?/i, '')
        .replace(/refresh_token=[^&]+&?/i, '')
        .replace(/provider_token=[^&]+&?/i, '')
        .replace(/expires_in=[^&]+&?/i, '')
        .replace(/token_type=[^&]+&?/i, '')
        .replace(/&$/, '');
      history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + cleanHash);
    }

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const changeLanguage = (lang: Language) => {
    setIsLangMenuOpen(false);
    if (lang === language) return;

    if (mode === AppMode.CHECKER && new URLSearchParams(window.location.search).has(ANALYSIS_QUERY_KEY)) {
      showToast(t.languageChangeBlockedChecker);
      return;
    }

    setIsLoadingLanguage(true);
    setTimeout(() => {
      setLanguage(lang);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      } catch {
        // ignore storage errors
      }
      setIsLoadingLanguage(false);
    }, 600);
  };

  const getLangLabel = (lang: Language) => {
    switch(lang) {
      case 'es': return 'Español';
      case 'en': return 'English';
      default: return 'English';
    }
  };

  const renderContent = () => {
    switch (mode) {
      case AppMode.STUDY:
        return <StudyMode setFocusMode={setFocusMode} language={language} theme={theme} setMode={navigateMode} />;
      case AppMode.TEST:
        return <TestMode language={language} theme={theme} />;
      case AppMode.CHECKER:
        return (
          <CheckerMode
            language={language}
            theme={theme}
            onShowCaptureMaintenance={() => openMaintenanceModal(t.captureMaintenanceBody, false)}
          />
        );
      case AppMode.INFO:
        return <InfoMode setMode={navigateMode} language={language} theme={theme} />;
      case AppMode.CERTIFICATE:
        return <CertificateMode language={language} theme={theme} setMode={navigateMode} />;
      case AppMode.ACCOUNT:
        return (
          <AccountSettings
            language={language}
            theme={theme}
            authUser={authUser}
            onBack={() => navigateMode(AppMode.CHECKER)}
            onUserUpdated={(user) => {
              setAuthUser(user);
              const name = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
              setAuthName(name);
            }}
          />
        );
      case AppMode.PLAN:
        return (
          <MyPlan
            theme={theme}
            language={language}
            t={t}
            onBack={() => navigateMode(AppMode.CHECKER)}
            onPricingCta={handlePricingCta}
          />
        );
      case AppMode.API_KEY:
        return (
          <ApiKeyScreen
            theme={theme}
            language={language}
            t={t}
            onBack={() => navigateMode(AppMode.CHECKER)}
          />
        );
      case AppMode.BILLING:
        return (
          <BillingScreen
            theme={theme}
            language={language}
            t={t}
            onBack={() => navigateMode(AppMode.CHECKER)}
            onGoToPlan={() => navigateMode(AppMode.PLAN)}
          />
        );
      case AppMode.TERMS:
        return (
          <TermsScreen
            theme={theme}
            language={language}
            t={t}
            onBack={() => navigateMode(AppMode.CHECKER)}
          />
        );
      case AppMode.SIGNIN:
        return <SignIn language={language} theme={theme} setMode={navigateMode} />;
      default:
        return (
          <Home
            setMode={navigateMode}
            t={t}
            theme={theme}
            language={language}
            onOpenAuth={openAuth}
            onPricingCta={handlePricingCta}
            authUser={authUser}
            userPlanId={userPlanId}
          />
        );
    }
  };

  useEffect(() => {
    if (!authUser) return;
    const path = normalizePath(window.location.pathname);
    const homePath = normalizePath(homeHref);
    if (path === homePath || mode === AppMode.HOME) {
      setAllowLandingAccess(false);
      navigateMode(AppMode.CHECKER);
    }
  }, [authUser, mode, homeHref]);

  useEffect(() => {
    if (!authUser) return;
    const storedPlan = getStoredCheckoutPlan();
    if (!storedPlan) return;
    clearPendingCheckoutPlan();
    createCheckoutSession(storedPlan);
  }, [authUser]);

  useEffect(() => {
    const syncCheckoutSession = async () => {
      const params = new URLSearchParams(window.location.search);
      const checkoutStatus = params.get('checkout');
      const sessionId = params.get('session_id');

      const cleanCheckoutParams = () => {
        const cleanParams = new URLSearchParams(window.location.search);
        cleanParams.delete('checkout');
        cleanParams.delete('session_id');
        const cleanQuery = cleanParams.toString();
        history.replaceState(null, '', `${appHref}${cleanQuery ? `?${cleanQuery}` : ''}`);
      };

      if (checkoutStatus === 'cancelled') {
        cleanCheckoutParams();
        return;
      }

      if (checkoutStatus !== 'success' || !sessionId || !authUser?.id) return;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token ?? null;
        if (!accessToken) return;

        setIsCheckoutRedirecting(true);
        const { data, error } = await supabase.functions.invoke('sync-checkout-session', {
          body: { sessionId },
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (error || !data?.ok) {
          console.error('[checkout] sync-checkout-session failed', { error, data, sessionId });
          showToast(t.checkoutSyncWarning);
        } else {
          const detail = {
            plan: data?.entitlements?.plan,
            credits_remaining: data?.entitlements?.credits_remaining,
          };
          window.dispatchEvent(new CustomEvent('aaaquest:credits-updated', { detail }));
          if (typeof data?.entitlements?.plan === 'number') {
            setUserPlanId(data.entitlements.plan);
          }
          if (typeof data?.entitlements?.credits_remaining === 'number') {
            setUserCreditsRemaining(data.entitlements.credits_remaining);
          }
          setIsHeaderEntitlementsLoading(false);
          showToast(t.checkoutSyncSuccess);
        }
      } finally {
        setIsCheckoutRedirecting(false);
        cleanCheckoutParams();
      }
    };

    syncCheckoutSession();
  }, [authUser?.id, appHref, t]);

  useEffect(() => {
    const loadHeaderEntitlements = async () => {
      if (!authUser) {
        setUserPlanId(null);
        setUserCreditsRemaining(null);
        setIsHeaderEntitlementsLoading(false);
        return;
      }
      setIsHeaderEntitlementsLoading(true);
      setUserPlanId(null);
      setUserCreditsRemaining(null);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token ?? null;
        if (!accessToken) {
          setUserPlanId(null);
          setUserCreditsRemaining(null);
          setIsHeaderEntitlementsLoading(false);
          return;
        }
        const { data, error } = await supabase.functions.invoke('billing-info', {
          body: { limit: 1 },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (error || !data?.entitlements) {
          setUserPlanId(null);
          setUserCreditsRemaining(null);
          setIsHeaderEntitlementsLoading(false);
          return;
        }

        const rawPlan = Number(data.entitlements.plan ?? 1);
        const resolvedPlan = [1, 2, 3, 4].includes(rawPlan) ? rawPlan : 1;
        const remaining = data.entitlements.credits_remaining;
        setUserPlanId(resolvedPlan);
        setUserCreditsRemaining(typeof remaining === 'number' ? remaining : PLAN_CREDITS[resolvedPlan]);
        setIsHeaderEntitlementsLoading(false);
      } catch {
        setUserPlanId(null);
        setUserCreditsRemaining(null);
        setIsHeaderEntitlementsLoading(false);
      }
    };
    loadHeaderEntitlements();
  }, [authUser?.id]);

  useEffect(() => {
    const handleCreditsUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ plan?: number; credits_remaining?: number }>).detail;
      if (!detail) return;
      const parsedPlan = Number(detail.plan ?? userPlanId ?? 1);
      const resolvedPlan = [1, 2, 3, 4].includes(parsedPlan) ? parsedPlan : 1;
      setUserPlanId(resolvedPlan);
      setIsHeaderEntitlementsLoading(false);
      if (typeof detail.credits_remaining === 'number' && Number.isFinite(detail.credits_remaining)) {
        setUserCreditsRemaining(detail.credits_remaining);
      }
    };

    window.addEventListener('aaaquest:credits-updated', handleCreditsUpdated as EventListener);
    return () => {
      window.removeEventListener('aaaquest:credits-updated', handleCreditsUpdated as EventListener);
    };
  }, [userPlanId]);

  useEffect(() => {
    if (!authChecked || authUser) return;
    const path = normalizePath(window.location.pathname);
    if (
      path === normalizePath(appHref)
      || path === normalizePath(accountHref)
      || path === normalizePath(planHref)
      || path === normalizePath(apiKeyHref)
      || path === normalizePath(billingHref)
      || path === normalizePath(termsHref)
      || mode === AppMode.CHECKER
      || mode === AppMode.PLAN
      || mode === AppMode.API_KEY
      || mode === AppMode.BILLING
      || mode === AppMode.TERMS
    ) {
      setAllowLandingAccess(true);
      window.location.replace(homeHref);
    }
  }, [authChecked, authUser, mode, appHref, accountHref, planHref, apiKeyHref, billingHref, termsHref, homeHref]);

  if (isLoadingLanguage) {
    const loaderText = t.changingLanguage;
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center font-inter ${theme === 'dark' ? 'bg-black text-white' : 'bg-slate-100 text-emerald-900'}`} role="status" aria-live="polite">
        <Loader text={loaderText} theme={theme} />
      </div>
    );
  }

  if (isCheckoutRedirecting) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center font-inter ${theme === 'dark' ? 'bg-black text-white' : 'bg-slate-100 text-emerald-900'}`} role="status" aria-live="polite">
        <Loader text={t.redirectingToStripe} theme={theme} />
      </div>
    );
  }

  // Theme-based Classes
  const mainContainerClasses = theme === 'dark' 
    ? "bg-black text-slate-100 selection:bg-emerald-500/30"
    : "bg-slate-50 text-slate-900 selection:bg-emerald-500/30";

  // Modern Floating Header Classes - Glassmorphism Update
  const headerClasses = theme === 'dark'
    ? "bg-slate-900/60 border border-white/10 shadow-2xl shadow-black/50 backdrop-blur-xl"
    : "bg-white/60 border border-slate-200/60 shadow-xl shadow-slate-200/60 backdrop-blur-xl";

  const logoClasses = theme === 'dark'
    ? "text-white group-hover:text-emerald-400"
    : "text-slate-900 group-hover:text-emerald-600"; 

  // Updated Hover: Clean white/slate-50 in light mode to avoid dark shadows
  // Added hover:border-black for light mode
  const navButtonClasses = theme === 'dark'
    ? "bg-slate-800/50 hover:bg-slate-700 border-slate-700 hover:border-white/20 text-slate-300 hover:text-white"
    : "bg-white hover:bg-slate-50 border-slate-200 hover:border-black text-slate-600 hover:text-emerald-700 shadow-sm";

  const hideFooter =
    Boolean(authUser)
    && [
      AppMode.CHECKER,
      AppMode.ACCOUNT,
      AppMode.PLAN,
      AppMode.API_KEY,
      AppMode.BILLING,
      AppMode.TERMS,
    ].includes(mode);

  const mainPaddingClasses = hideFooter ? 'pt-[150px] pb-16 sm:pb-8' : 'pt-32 pb-16 sm:pb-8';

  return (
    <div className={`min-h-screen flex flex-col font-inter transition-colors duration-500 ${mainContainerClasses}`}>
      <style>{`
        .toggle{cursor:pointer;display:inline-flex;align-items:center}
        .toggle-switch{display:inline-block;background:#2b2f3a;border-radius:999px;width:58px;height:32px;position:relative;vertical-align:middle;transition:background .25s}
        .toggle-switch:before{content:"";display:block;background:linear-gradient(to bottom,#fff 0%,#eee 100%);border-radius:999px;box-shadow:0 0 0 1px rgba(0,0,0,.25);width:24px;height:24px;position:absolute;top:4px;left:4px;transition:left .25s}
        .toggle:hover .toggle-switch:before{background:#fff;box-shadow:0 0 0 1px rgba(0,0,0,.5)}
        .toggle-checkbox{position:absolute;visibility:hidden}
        .toggle-checkbox:checked + .toggle-switch{background:#038759}
        .toggle-checkbox:checked + .toggle-switch:before{left:30px}
        .toggle-switch{max-width:100%}
      `}</style>
      {showCookieBanner && (
      <div className="fixed bottom-4 right-4 z-[76]">
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          className="w-[min(480px,calc(100vw-2rem))] rounded-3xl border border-white/10 bg-slate-950 px-6 py-5 text-white shadow-2xl shadow-black/50"
        >
          {!showCookiePrefs ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center">
                      <img src={faviconIcon} alt="AAAQuest" className="h-7 w-7" />
                    </div>
                    <span id="cookie-banner-title" className="text-[18px] font-semibold text-white">AAAQuest</span>
                  </div>
                  <p className="text-sm text-slate-400">
                    {t.cookieDescription}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      persistCookiePrefs({ analytics: true, marketing: true });
                      setCookieAnalytics(true);
                      setCookieMarketing(true);
                      setShowCookieBanner(false);
                    }}
                    className="w-full rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-950"
                  >
                    {t.cookieAcceptAll}
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        persistCookiePrefs({ analytics: false, marketing: false });
                        setCookieAnalytics(false);
                        setCookieMarketing(false);
                        setShowCookieBanner(false);
                      }}
                      className="w-full rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white"
                    >
                      {t.cookieRejectAll}
                    </button>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        setShowCookiePrefs(true);
                      }}
                      className="w-full rounded-full border border-white/10 px-3 py-2 text-xs font-semibold text-white"
                    >
                      {t.cookiePreferences}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="space-y-3">
                  <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                      <div>
                        <p id="cookie-essential-title" className="text-sm font-semibold">{t.cookieEssentialTitle} <span className="text-xs font-normal text-slate-400">{t.cookieRequiredLabel}</span></p>
                        <p id="cookie-essential-desc" className="mt-2 text-xs text-slate-400">{t.cookieEssentialDesc}</p>
                      </div>
                      <label className="toggle">
                        <input
                          className="toggle-checkbox"
                          type="checkbox"
                          checked
                          disabled
                          aria-disabled="true"
                          aria-labelledby="cookie-essential-title"
                          aria-describedby="cookie-essential-desc"
                        />
                        <div className="toggle-switch" aria-hidden="true" />
                      </label>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                      <div>
                        <p id="cookie-analytics-title" className="text-sm font-semibold">{t.cookieAnalyticsTitle}</p>
                        <p id="cookie-analytics-desc" className="mt-2 text-xs text-slate-400">{t.cookieAnalyticsDesc}</p>
                      </div>
                      <label className="toggle">
                        <input
                          className="toggle-checkbox"
                          type="checkbox"
                          checked={cookieAnalytics}
                          onChange={() => setCookieAnalytics((prev) => !prev)}
                          aria-labelledby="cookie-analytics-title"
                          aria-describedby="cookie-analytics-desc"
                        />
                        <div className="toggle-switch" aria-hidden="true" />
                      </label>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                      <div>
                        <p id="cookie-marketing-title" className="text-sm font-semibold">{t.cookieMarketingTitle}</p>
                        <p id="cookie-marketing-desc" className="mt-2 text-xs text-slate-400">{t.cookieMarketingDesc}</p>
                      </div>
                      <label className="toggle">
                        <input
                          className="toggle-checkbox"
                          type="checkbox"
                          checked={cookieMarketing}
                          onChange={() => setCookieMarketing((prev) => !prev)}
                          aria-labelledby="cookie-marketing-title"
                          aria-describedby="cookie-marketing-desc"
                        />
                        <div className="toggle-switch" aria-hidden="true" />
                      </label>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    persistCookiePrefs({ analytics: cookieAnalytics, marketing: cookieMarketing });
                    setShowCookiePrefs(false);
                    setShowCookieBanner(false);
                  }}
                  className="w-full rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900"
                >
                  {t.cookieSavePreferences}
                </button>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowCookiePrefs(false);
                  }}
                  className="w-full text-center text-sm font-semibold text-[#038759]"
                >
                  {t.accountBack}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {showMaintenance && (
        <>
          <div
            className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => setShowMaintenance(false)}
          />
          <div
            ref={maintenanceDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="maintenance-title"
            tabIndex={-1}
            className="fixed left-1/2 top-1/2 z-[91] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/10 bg-slate-950 px-8 py-8 text-center text-white shadow-2xl shadow-black/60"
          >
            <button
              type="button"
              aria-label={t.authCloseLabel}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              onClick={() => setShowMaintenance(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <h2 id="maintenance-title" className="text-2xl font-semibold">{t.maintenanceTitle}</h2>
            <p className="mt-3 text-sm text-slate-200">
              {activeMaintenanceBody}
              {showMaintenanceLink && (
                <>
                  {' '}
                  <a
                    href="https://www.linkedin.com/in/victorsaizalfageme/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-300 underline underline-offset-4"
                  >
                    {t.maintenanceLinkLabel}
                  </a>
                  .
                </>
              )}
            </p>
          </div>
        </>
      )}
      {showPricingContactModal && (
        <>
          <div
            className="fixed inset-0 z-[92] bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => setShowPricingContactModal(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="pricing-contact-title"
            className="fixed left-1/2 top-1/2 z-[93] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/10 bg-slate-950 px-6 py-6 text-white shadow-2xl shadow-black/60 md:px-8"
          >
            <button
              type="button"
              aria-label={t.authCloseLabel}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              onClick={() => setShowPricingContactModal(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <h2 id="pricing-contact-title" className="pr-10 text-xl font-semibold md:text-2xl">
              {t.pricingContactModalTitle}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-200">
              {t.pricingContactModalBody}
            </p>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPricingContactModal(false);
                  window.open('https://www.linkedin.com/in/victorsaizalfageme/', '_blank', 'noopener,noreferrer');
                }}
                className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {t.pricingContactModalSecondaryCta}
              </button>
            </div>
          </div>
        </>
      )}
      {showWelcomeModal && authUser && (
        <>
          <div
            className="fixed inset-0 z-[92] bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => setShowWelcomeModal(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-modal-title"
            className="fixed left-1/2 top-1/2 z-[93] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-white/10 bg-slate-950 px-6 py-6 text-white shadow-2xl shadow-black/60 md:px-8"
          >
            <button
              type="button"
              aria-label={t.authCloseLabel}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              onClick={() => setShowWelcomeModal(false)}
            >
              <X className="h-4 w-4" />
            </button>
            <h2 id="welcome-modal-title" className="pr-10 text-xl font-semibold md:text-2xl">
              {t.welcomeModalTitle}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-200">
              {t.welcomeModalBodyPrimary}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              {t.welcomeModalBodySecondary}{' '}
              <a
                href="https://www.linkedin.com/in/victorsaizalfageme/"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-300 underline underline-offset-4"
              >
                Victor Saiz Alfageme
              </a>
              . {t.welcomeModalBodyTertiary}
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowWelcomeModal(false)}
                className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                {t.welcomeModalCloseCta}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowWelcomeModal(false);
                  openContact();
                }}
                className="rounded-full bg-[#038759] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#026e49]"
              >
                {t.welcomeModalContactCta}
              </button>
            </div>
          </div>
        </>
      )}
      <div>
        {/* Dynamic Background Grid */}
        <div className="fixed inset-0 z-0 opacity-20 pointer-events-none" 
             style={{
               backgroundImage: theme === 'dark' 
                  ? 'linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px)'
                  : 'linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px)',
               backgroundSize: '50px 50px'
             }} 
             aria-hidden="true"
        />

      {/* Modern Floating Header - Width aligned to Content (max-w-5xl) */}
      <header className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-5xl rounded-full transition-all duration-300 h-16 px-6 flex items-center justify-between ${headerClasses}`}>
          <button
            type="button"
            onClick={handleHeaderLogoClick}
            className="flex items-center gap-3 font-bold text-lg rounded-full px-2 py-1 transition-opacity hover:opacity-90"
            aria-label={t.goToApp}
          >
            <div className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg bg-gradient-to-tr from-emerald-500 to-green-600">
              <img src={faviconIcon} alt="Logo AAAQuest" className="h-5 w-5" />
            </div>
            <span className={`tracking-tight transition-colors hidden sm:inline ${logoClasses}`}>{t.appTitle}</span>
          </button>
          
          <nav className="flex items-center gap-2" aria-label="Main Navigation">
            {mode === AppMode.HOME && (
              <a
                href="#pricing"
                onClick={(event) => {
                  event.preventDefault();
                  const pricing = document.querySelector('#pricing');
                  if (pricing) {
                    pricing.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } else {
                    window.location.hash = '#pricing';
                  }
                }}
                className={`flex items-center justify-center gap-2 px-4 h-10 rounded-full transition-all active:scale-95 text-xs font-normal ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                aria-label={t.pricingNav}
              >
                <span className="pt-0.5 leading-none">{t.pricingNav}</span>
              </a>
            )}
            {!authUser && (
              <button
                type="button"
                onClick={() => openAuth('signin')}
                className="flex items-center justify-center px-4 h-10 border rounded-full transition-all active:scale-95 text-xs font-normal text-white border-[#038759] bg-[#038759] hover:bg-[#026e49]"
                aria-label={t.signIn}
              >
                <span className="pt-0.5 leading-none">{t.signIn}</span>
              </button>
            )}
            {authUser && isHeaderEntitlementsLoading && <CreditsBadgeSkeleton theme={theme} />}
            {authUser && !isHeaderEntitlementsLoading && userPlanId !== null && userCreditsRemaining !== null && (
              <CreditsBadge
                theme={theme}
                t={t}
                planId={userPlanId}
                creditsRemaining={userCreditsRemaining}
                onClick={() => navigateMode(AppMode.PLAN)}
              />
            )}

            {/* Language Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className={`flex items-center justify-center gap-2 px-4 h-10 border rounded-full transition-all active:scale-95 text-xs font-bold ${navButtonClasses}`}
                aria-expanded={isLangMenuOpen}
                aria-haspopup="true"
                aria-label={`${t.currentLanguageAriaPrefix} ${getLangLabel(language)}. ${t.currentLanguageAriaSuffix}`}
              >
                <Globe className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden sm:inline pt-0.5 leading-none">{getLangLabel(language)}</span>
                <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isLangMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsLangMenuOpen(false)} aria-hidden="true" />
                  <div 
                    className={`absolute right-0 mt-3 w-40 border rounded-2xl shadow-xl overflow-hidden z-50 animate-fade-in ${
                      theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-slate-200/50'
                    }`}
                    role="menu"
                  >
                    {(['es', 'en'] as Language[]).map((lang) => (
                      <button 
                        key={lang}
                        onClick={() => changeLanguage(lang)} 
                        className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center justify-between ${
                            theme === 'dark'
                                ? (language === lang ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-white')
                                : (language === lang ? 'bg-emerald-50 text-emerald-800 font-bold' : 'text-slate-600 hover:bg-slate-50 hover:text-emerald-700')
                        }`}
                        role="menuitem"
                      >
                        {getLangLabel(lang)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {authUser && (
              <div className="relative">
                {(() => {
                  const avatarUrl =
                    authUser?.user_metadata?.picture
                    || authUser?.user_metadata?.avatar_url
                    || '';
                  return (
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-white shadow-lg active:scale-95 ${avatarUrl ? 'bg-slate-900/40' : 'bg-gradient-to-tr from-emerald-500 to-teal-500'}`}
                  aria-haspopup="true"
                  aria-expanded={isUserMenuOpen}
                  aria-label={t.userMenuAria}
                  ref={userMenuButtonRef}
                >
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold uppercase">{(authName || authUser.email || 'U').slice(0, 1)}</span>
                  )}
                </button>
                  );
                })()}
                {isUserMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsUserMenuOpen(false)} aria-hidden="true" />
                    <div
                      ref={userMenuRef}
                      className={`absolute right-0 mt-3 w-64 border rounded-2xl shadow-xl overflow-hidden z-50 ${theme === 'dark' ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200 shadow-slate-200/50'}`}
                    >
                      <div className={`px-4 py-3 ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>
                        <div className="text-sm font-semibold">{authName || authUser.email?.split('@')[0]}</div>
                        <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'} whitespace-nowrap`}>
                          {authUser.email}
                        </div>
                      </div>
                      <div className={`h-px ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigateMode(AppMode.ACCOUNT);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuAccount}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigateMode(AppMode.PLAN);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuPlan}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigateMode(AppMode.API_KEY);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuApiKey}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigateMode(AppMode.BILLING);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuBilling}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigateMode(AppMode.TERMS);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuTerms}
                      </button>
                      <div className={`h-px ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
                      <button
                        type="button"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          openContact();
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.userMenuContact}
                      </button>
                      <div className={`h-px ${theme === 'dark' ? 'bg-white/10' : 'bg-slate-200'}`} />
                      <button
                        type="button"
                        onClick={async () => {
                          setIsUserMenuOpen(false);
                        await supabase.auth.signOut();
                        setAuthUser(null);
                        setAuthName('');
                        setShowWelcomeModal(false);
                        setAllowLandingAccess(true);
                        navigateMode(AppMode.HOME);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-2 ${theme === 'dark' ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        {t.signOutButton}
                        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M12.9999 2C10.2385 2 7.99991 4.23858 7.99991 7C7.99991 7.55228 8.44762 8 8.99991 8C9.55219 8 9.99991 7.55228 9.99991 7C9.99991 5.34315 11.3431 4 12.9999 4H16.9999C18.6568 4 19.9999 5.34315 19.9999 7V17C19.9999 18.6569 18.6568 20 16.9999 20H12.9999C11.3431 20 9.99991 18.6569 9.99991 17C9.99991 16.4477 9.55219 16 8.99991 16C8.44762 16 7.99991 16.4477 7.99991 17C7.99991 19.7614 10.2385 22 12.9999 22H16.9999C19.7613 22 21.9999 19.7614 21.9999 17V7C21.9999 4.23858 19.7613 2 16.9999 2H12.9999Z" fill="currentColor"></path>
                          <path d="M13.9999 11C14.5522 11 14.9999 11.4477 14.9999 12C14.9999 12.5523 14.5522 13 13.9999 13V11Z" fill="currentColor"></path>
                          <path d="M5.71783 11C5.80685 10.8902 5.89214 10.7837 5.97282 10.682C6.21831 10.3723 6.42615 10.1004 6.57291 9.90549C6.64636 9.80795 6.70468 9.72946 6.74495 9.67492L6.79152 9.61162L6.804 9.59454L6.80842 9.58848C6.80846 9.58842 6.80892 9.58778 5.99991 9L6.80842 9.58848C7.13304 9.14167 7.0345 8.51561 6.58769 8.19098C6.14091 7.86637 5.51558 7.9654 5.19094 8.41215L5.18812 8.41602L5.17788 8.43002L5.13612 8.48679C5.09918 8.53682 5.04456 8.61033 4.97516 8.7025C4.83623 8.88702 4.63874 9.14542 4.40567 9.43937C3.93443 10.0337 3.33759 10.7481 2.7928 11.2929L2.08569 12L2.7928 12.7071C3.33759 13.2519 3.93443 13.9663 4.40567 14.5606C4.63874 14.8546 4.83623 15.113 4.97516 15.2975C5.04456 15.3897 5.09918 15.4632 5.13612 15.5132L5.17788 15.57L5.18812 15.584L5.19045 15.5872C5.51509 16.0339 6.14091 16.1336 6.58769 15.809C7.0345 15.4844 7.13355 14.859 6.80892 14.4122L5.99991 15C6.80892 14.4122 6.80897 14.4123 6.80892 14.4122L6.804 14.4055L6.79152 14.3884L6.74495 14.3251C6.70468 14.2705 6.64636 14.1921 6.57291 14.0945C6.42615 13.8996 6.21831 13.6277 5.97282 13.318C5.89214 13.2163 5.80685 13.1098 5.71783 13H13.9999V11H5.71783Z" fill="currentColor"></path>
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

          </nav>
      </header>

      {toastMessage && (
        <div className="fixed top-32 right-6 z-[60]" role="alert" aria-live="polite">
          <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-xl ${theme === 'dark' ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
            <span className="text-sm">{toastMessage}</span>
            <button
              type="button"
              onClick={() => setToastMessage(null)}
              className={`w-7 h-7 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-white/10 hover:bg-white/20' : 'bg-slate-100 hover:bg-slate-200'}`}
              aria-label={t.closeNotificationAria}
              ref={toastCloseRef}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content - Added top padding for header and bottom padding for fixed footer */}
      <main className={`container mx-auto px-4 ${mainPaddingClasses} relative z-10`} id="main-content">
        {renderContent()}
      </main>

      {!hideFooter && (
        <footer className={`w-full border-t py-4 backdrop-blur-md transition-all duration-300 ${theme === 'dark' ? 'border-white/5 bg-black/80' : 'border-slate-200 bg-white/80'}`}>
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-center text-center">
            <p className={`text-xs md:text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
              Victor Saiz
            </p>
          </div>
        </footer>
      )}

      <AuthModal
        isOpen={isAuthOpen}
        entry={authEntry}
        onClose={() => {
          setIsAuthOpen(false);
          clearPendingCheckoutPlan();
        }}
        language={language}
        theme={theme}
      />
      <ContactModal
        isOpen={isContactOpen}
        onClose={() => setIsContactOpen(false)}
        userEmail={authUser?.email ?? null}
        userName={authName}
        language={language}
        theme={theme}
        onRequestAuth={() => openAuth('signin')}
      />
      </div>
    </div>
  );
};

const MyPlan: React.FC<{
  theme: Theme;
  language: Language;
  t: any;
  onBack: () => void;
  onPricingCta: (url?: string, planId?: number) => void;
}> = ({ theme, language, t, onBack, onPricingCta }) => {
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
  const [creditsResetAt, setCreditsResetAt] = useState<string | null>(null);
  const starterPrice = 15;
  const proPrice = 95;

  const formatCurrency = (value: number) =>
    value.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formatEuro = (value: number) => `${formatCurrency(value)} EUR`;
  const currentPlanLabel = language === 'es' ? 'Tu plan actual' : 'Your current plan';
  const planName = (planId?: number | null) => {
    if (planId === 2) return t.pricingStarterName;
    if (planId === 3) return t.pricingProName;
    if (planId === 4) return t.pricingEnterpriseName;
    return t.freePlanName;
  };
  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '—';

  useEffect(() => {
    const loadCurrentPlan = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? null;
      if (!accessToken) return;
      setIsPlanLoading(true);
      const { data, error } = await supabase.functions.invoke('billing-info', {
        body: { limit: 1 },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!error && data?.entitlements?.plan) {
        const parsed = Number(data.entitlements.plan);
        setCurrentPlanId(Number.isFinite(parsed) ? parsed : null);
      }
      if (!error && data?.entitlements) {
        setCreditsRemaining(data.entitlements.credits_remaining ?? null);
        setCreditsResetAt(data.entitlements.credits_reset_at ?? null);
      }
      setIsPlanLoading(false);
    };
    loadCurrentPlan();
  }, []);

  /*
  const pricingPlans = [
    {
      id: 'starter',
      planId: 2,
      name: t.pricingStarterName,
      desc: t.pricingStarterDesc,
      badge: t.pricingStarterBadge,
      price: starterPrice,
      cta: t.pricingStarterCta,
      ctaHref: 'https://buy.stripe.com/bJecN5d7th0I8f308bfUQ05',
      includesLabel: t.pricingStarterIncludes,
      features: t.pricingStarterFeatures,
      highlight: false,
      requiresAuth: true
    },
    {
      id: 'pro',
      planId: 3,
      name: t.pricingProName,
      desc: t.pricingProDesc,
      badge: t.pricingProBadge,
      price: proPrice,
      cta: t.pricingProCta,
      ctaHref: 'https://buy.stripe.com/5kQfZhc3p9yg9j71cffUQ04',
      includesLabel: t.pricingProIncludes,
      features: t.pricingProFeatures,
      highlight: true,
      requiresAuth: true
    },
    {
      id: 'enterprise',
      planId: 4,
      name: t.pricingEnterpriseName,
      desc: t.pricingEnterpriseDesc,
      badge: t.pricingEnterpriseBadge,
      priceLabel: t.pricingEnterprisePrice,
      cta: t.pricingEnterpriseCta,
      ctaHref: 'mailto:victorsaizalfageme@gmail.com',
      includesLabel: t.pricingEnterpriseIncludes,
      features: t.pricingEnterpriseFeatures,
      highlight: false
    }
  ];
  */

  const pricingPlans = [
    {
      id: 'trial',
      planId: 1,
      name: t.pricingFreeTrialName,
      desc: t.pricingFreeTrialDesc,
      badge: t.pricingFreeTrialBadge,
      priceLabel: t.pricingFreeTrialPrice,
      cta: t.pricingFreeTrialCta,
      ctaHref: 'signup',
      includesLabel: t.pricingStarterIncludes,
      features: language === 'es'
        ? ['5 análisis de pantalla', '1 usuario', 'Exportación a PDF', 'Documentación completa']
        : ['5 screen analyses', '1 user', 'PDF export', 'Complete documentation'],
      highlight: false,
    },
    {
      id: 'custom',
      planId: 4,
      name: t.pricingCustomName,
      desc: t.pricingCustomDesc,
      badge: t.pricingCustomBadge,
      priceLabel: t.pricingEnterprisePrice,
      cta: t.pricingCustomCta,
      ctaHref: 'mailto:victorsaizalfageme@gmail.com',
      includesLabel: t.pricingEnterpriseIncludes,
      features: t.pricingCustomFeatures,
      highlight: true,
    }
  ];

  return (
    <div className="relative flex flex-col items-center justify-start w-full pb-8">
      <div className="w-full max-w-5xl mx-auto px-4 pb-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={onBack}
              className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              <span aria-hidden="true">←</span>
              {t.accountBack}
            </button>
            <h1 className={`mt-3 text-2xl font-semibold sm:text-3xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.myPlanTitle}</h1>
          </div>
        </div>

        <Reveal>
          <div className="text-center max-w-3xl mx-auto mt-12">
          </div>
        </Reveal>

        <section id="plan-cards" className="w-full mt-12 scroll-mt-32">
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            {pricingPlans.map((plan: any, index: number) => (
              <Reveal key={plan.id} delay={index * 120}>
                <div
                  className={`rounded-3xl border p-6 sm:p-8 lg:p-10 h-full flex flex-col relative overflow-hidden ${
                    plan.highlight
                      ? 'border-emerald-300/30 bg-gradient-to-br from-[#0c2920] via-[#0a221a] to-[#081a14] shadow-[0_0_32px_rgba(16,185,129,0.2)] ring-1 ring-emerald-300/40 before:absolute before:inset-0 before:rounded-3xl before:shadow-[inset_0_0_0_1px_rgba(16,185,129,0.28)] before:pointer-events-none'
                      : theme === 'dark'
                        ? 'border-white/10 bg-gradient-to-b from-slate-900/90 to-slate-950/90 shadow-2xl shadow-black/40'
                        : 'border-slate-200 bg-white shadow-xl shadow-slate-200/40'
                  }`}
                >
                  <div className="min-h-[92px]">
                    <div>
                      <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                      <p className={`mt-2 text-sm min-h-[48px] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{plan.desc}</p>
                    </div>
                  </div>

                  <div className="mt-6 min-h-[88px]">
                    <div className={`mt-2 text-2xl font-black sm:text-3xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                      {plan.price ? formatEuro(plan.price) : plan.priceLabel}
                      {plan.price && (
                        <span className={`text-base font-normal ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{t.pricingPerMonth}</span>
                      )}
                    </div>
                    <span className={`mt-2 block text-[10px] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {plan.badge}
                    </span>
                  </div>

                  <div className="mt-2 min-h-[76px] pt-4">
                    {plan.planId && currentPlanId === plan.planId ? (
                      <button
                        type="button"
                        aria-disabled="true"
                        onClick={(event) => event.preventDefault()}
                        className={`w-full rounded-full py-2 text-sm font-normal transition opacity-70 ${
                          theme === 'dark'
                            ? 'bg-white/70 text-slate-900'
                            : 'bg-slate-200 text-slate-700'
                        } ${isPlanLoading ? 'animate-pulse' : ''}`}
                      >
                        {currentPlanLabel}
                      </button>
                    ) : plan.ctaHref ? (
                      <button
                        type="button"
                        onClick={() => onPricingCta(plan.ctaHref, plan.planId)}
                        className={`w-full rounded-full py-2 text-sm font-normal transition ${
                          plan.highlight
                            ? 'bg-[#038759] text-white ring-1 ring-emerald-300/60 shadow-[0_0_16px_rgba(16,185,129,0.35)]'
                            : theme === 'dark'
                              ? 'bg-white text-slate-900'
                              : 'bg-slate-900 text-white'
                        }`}
                      >
                        {plan.cta}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`w-full rounded-full py-2 text-sm font-normal transition ${
                          theme === 'dark' ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                      >
                        {plan.cta}
                      </button>
                    )}
                  </div>

                  <div className={`mt-4 border-t pt-5 flex-1 ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>
                      {plan.includesLabel}
                    </p>
                    <ul className="mt-4 space-y-3">
                      {plan.features.map((feature: string) => (
                        <li
                          key={feature}
                          className={`flex items-start gap-3 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                          <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${theme === 'dark' ? 'bg-slate-300' : 'bg-slate-500'}`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                </div>
              </Reveal>
            ))}
          </div>

          <p className={`mt-6 text-center text-[10px] px-4 sm:px-12 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{t.pricingFootnote}</p>
        </section>
      </div>
    </div>
  );
};

const Home: React.FC<{
  setMode: (m: AppMode) => void;
  t: any;
  theme: Theme;
  language: Language;
  onOpenAuth: (entry: 'signup' | 'signin') => void;
  onPricingCta: (url?: string, planId?: number) => void;
  authUser: any | null;
  userPlanId: number | null;
}> = ({ setMode, t, theme, language, onOpenAuth, onPricingCta, authUser, userPlanId }) => {
  const starterPrice = 15;
  const proPrice = 95;
  const currentPlanLabel = language === 'es' ? 'Tu plan actual' : 'Your current plan';
  const [openFaqIndexes, setOpenFaqIndexes] = useState<Set<number>>(new Set());
  const homeVideoRef = useRef<HTMLVideoElement | null>(null);
  const [homeVideoReady, setHomeVideoReady] = useState(false);

  useEffect(() => {
    const video = homeVideoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setHomeVideoReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const formatCurrency = (value: number) =>
    value.toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const formatEuro = (value: number) => `${formatCurrency(value)} EUR`;

  const featureCards = [
    { icon: Clock, ...t.features[0] },
    { icon: FileText, ...t.features[1] },
    { icon: Layers, ...t.features[2] },
    { icon: RefreshCcw, ...t.features[3] }
  ];

  /*
  const pricingPlans = [
    {
      id: 'starter',
      planId: 2,
      name: t.pricingStarterName,
      desc: t.pricingStarterDesc,
      badge: t.pricingStarterBadge,
      price: starterPrice,
      cta: t.pricingStarterCta,
      ctaHref: 'https://buy.stripe.com/bJecN5d7th0I8f308bfUQ05',
      includesLabel: t.pricingStarterIncludes,
      features: t.pricingStarterFeatures,
      highlight: false,
      requiresAuth: true
    },
    {
      id: 'pro',
      planId: 3,
      name: t.pricingProName,
      desc: t.pricingProDesc,
      badge: t.pricingProBadge,
      price: proPrice,
      cta: t.pricingProCta,
      ctaHref: 'https://buy.stripe.com/5kQfZhc3p9yg9j71cffUQ04',
      includesLabel: t.pricingProIncludes,
      features: t.pricingProFeatures,
      highlight: true,
      requiresAuth: true
    },
    {
      id: 'enterprise',
      name: t.pricingEnterpriseName,
      desc: t.pricingEnterpriseDesc,
      badge: t.pricingEnterpriseBadge,
      priceLabel: t.pricingEnterprisePrice,
      cta: t.pricingEnterpriseCta,
      ctaHref: 'mailto:victorsaizalfageme@gmail.com',
      includesLabel: t.pricingEnterpriseIncludes,
      features: t.pricingEnterpriseFeatures,
      highlight: false
    }
  ];
  */

  const pricingPlans = [
    {
      id: 'trial',
      planId: 1,
      name: t.pricingFreeTrialName,
      desc: t.pricingFreeTrialDesc,
      badge: t.pricingFreeTrialBadge,
      priceLabel: t.pricingFreeTrialPrice,
      cta: t.pricingFreeTrialCta,
      ctaHref: 'signup',
      includesLabel: t.pricingStarterIncludes,
      features: language === 'es'
        ? ['5 análisis de pantalla', '1 usuario', 'Exportación a PDF', 'Documentación completa']
        : ['5 screen analyses', '1 user', 'PDF export', 'Complete documentation'],
      highlight: false,
    },
    {
      id: 'custom',
      planId: 4,
      name: t.pricingCustomName,
      desc: t.pricingCustomDesc,
      badge: t.pricingCustomBadge,
      priceLabel: t.pricingEnterprisePrice,
      cta: t.pricingCustomCta,
      ctaHref: 'mailto:victorsaizalfageme@gmail.com',
      includesLabel: t.pricingEnterpriseIncludes,
      features: t.pricingCustomFeatures,
      highlight: true,
    }
  ];

  return (
    <div className="relative flex flex-col items-center justify-center min-h-[60vh] w-full">
      
      {/* Blurred Background Visual */}
      <div className="fixed inset-0 z-0 flex items-center justify-center pointer-events-none select-none overflow-hidden" aria-hidden="true">
         <div className={`transform scale-[2.5] origin-center blur-2xl filter saturate-50 ${theme === 'dark' ? 'opacity-30' : 'opacity-10'}`}>
           <NeuralCore />
         </div>
      </div>

      {/* Main Foreground Container */}
      <div className="relative z-10 w-full max-w-6xl px-4">
        <div className="flex flex-col items-center text-center mt-6 md:mt-10">
          
          <div className="space-y-8 mb-16 max-w-3xl flex flex-col items-center">
            
            <Reveal>
              <div className="flex flex-col items-center gap-4">
                <div className="relative inline-block">
                <h1 className={`text-4xl md:text-6xl font-black tracking-tight drop-shadow-xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                  <span>{t.homeTitle}</span>
                </h1>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <p className={`text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                {t.homeSubtitle}
              </p>
            </Reveal>

            <Reveal delay={300}>
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="button"
                  onClick={() => onOpenAuth('signup')}
                  className="flex items-center gap-2 px-6 py-2 rounded-full transition-all text-sm font-normal border border-[#038759] bg-[#038759] text-white hover:bg-[#026e49] active:scale-95"
                  aria-label={t.signUpNav}
                >
                  <UserPlus className="w-4 h-4" />
                  {t.signUpForFree}
                </button>
                <button
                  onClick={() => {
                    if (authUser) {
                      setMode(AppMode.CHECKER);
                    } else {
                      onOpenAuth('signin');
                    }
                  }}
                  className={`flex items-center gap-2 px-6 py-2 rounded-full transition-all text-sm font-normal border active:scale-95 ${theme === 'dark' ? 'bg-slate-900 text-white border-slate-700 hover:bg-slate-800' : 'bg-slate-100 text-slate-900 border-slate-200 hover:bg-slate-200'}`}
                >
                  <LogIn className="w-4 h-4" />
                  {t.launchApp}
                </button>
              </div>
            </Reveal>
          </div>

        </div>

        <div className="w-full max-w-6xl mx-auto mt-10 md:mt-14">
          <div className={`rounded-[2rem] overflow-hidden border ${theme === 'dark' ? 'bg-slate-900 border-white/10 shadow-2xl shadow-black/50' : 'bg-white border-slate-200 shadow-2xl shadow-slate-200/70'}`}>
            <div className="relative w-full aspect-video">
            <video
              ref={homeVideoRef}
              className="absolute inset-0 w-full h-full object-cover"
              src={homeVideoReady ? homeVideoSrc : undefined}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              poster="/files/Analizador.png"
              width={1280}
              height={720}
              aria-label={`${t.checkerTitle} demo`}
            />
            </div>
          </div>
        </div>

        <section className="w-full mt-28">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className={`mt-6 text-3xl md:text-4xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                {t.featuresTitle}
              </h2>
              <p className={`mt-4 text-base md:text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                {t.featuresSubtitle}
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 items-stretch">
            {featureCards.map((feature: any, index: number) => {
              const Icon = feature.icon;
              return (
                <Reveal key={feature.title} delay={index * 100}>
                  <div className={`group rounded-3xl border p-6 transition-all tilt-hover h-full ${theme === 'dark' ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white shadow-xl shadow-slate-200/40'}`}>
                    <div className="w-12 h-12 rounded-2xl bg-black/50 border border-white/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#9ff0cf]" />
                    </div>
                    <h3 className={`mt-6 text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{feature.title}</h3>
                    <p className={`mt-3 text-sm leading-relaxed ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{feature.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section className="w-full mt-40 sm:mt-32">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className={`text-3xl md:text-4xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.workflowTitle}</h2>
              <p className={`mt-4 text-base md:text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{t.workflowSubtitle}</p>
            </div>
          </Reveal>

          <div className="relative mt-12">
            <div className="grid lg:grid-cols-3 gap-6">
              {t.workflowSteps.map((step: any, index: number) => (
                  <Reveal key={step.title} delay={index * 120}>
                    <div className="relative p-8 text-center">
                      <div className="mx-auto w-14 h-14 rounded-full bg-[#038759] text-white flex items-center justify-center text-lg font-semibold shadow-lg shadow-emerald-500/20">
                        {index + 1}
                      </div>
                      <h3 className={`mt-6 text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{step.title}</h3>
                      <p className={`mt-3 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{step.desc}</p>
                    </div>
                  </Reveal>
                ))}
            </div>
          </div>
        </section>

        <section className="w-full mt-32">
          <div className={`rounded-[2.5rem] border px-8 md:px-12 relative overflow-hidden ${theme === 'dark' ? 'border-white/10 bg-slate-900/70' : 'border-slate-200 bg-white shadow-xl shadow-slate-200/40'}`}>
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-10 right-10 w-40 h-40 rounded-full bg-[#038759]/20 blur-3xl" />
              <div className="absolute bottom-0 left-10 w-56 h-56 rounded-full bg-white/5 blur-3xl" />
            </div>
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center relative">
              <Reveal>
                <div>
                  <div className="flex items-center gap-3">
                    <img src={figmaLogo} alt="Figma" className="w-[60px] h-[60px]" />
                    <h2 className={`text-3xl md:text-4xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.pluginTitle}</h2>
                  </div>
                  <p className={`mt-4 text-base md:text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{t.pluginSubtitle}</p>
                  <ul className="mt-6 space-y-3">
                    {t.pluginBullets.map((bullet: string) => (
                      <li key={bullet} className={`flex items-center gap-3 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                        <span className="w-6 h-6 rounded-full bg-[#038759] text-white flex items-center justify-center text-xs">✓</span>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <span className={`text-xs uppercase tracking-widest ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{t.pluginNote}</span>
                  </div>
                </div>
              </Reveal>

              <Reveal delay={150}>
                <div className="relative w-full max-w-[420px] aspect-square rounded-[2rem] overflow-hidden mx-auto">
                  <img
                    src={pluginImage}
                    alt={t.pluginTitle}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section id="pricing" className="w-full mt-32 scroll-mt-32">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className={`text-3xl md:text-4xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.pricingTitle}</h2>
              <p className={`mt-4 text-base md:text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{t.pricingSubtitle}</p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-6 mt-12">
            {pricingPlans.map((plan: any, index: number) => (
              <Reveal key={plan.id} delay={index * 120}>
                <div
                  className={`rounded-3xl border p-6 sm:p-8 lg:p-10 h-full flex flex-col relative overflow-hidden ${
                    plan.highlight
                      ? 'border-emerald-300/30 bg-gradient-to-br from-[#0c2920] via-[#0a221a] to-[#081a14] shadow-[0_0_32px_rgba(16,185,129,0.2)] ring-1 ring-emerald-300/40 before:absolute before:inset-0 before:rounded-3xl before:shadow-[inset_0_0_0_1px_rgba(16,185,129,0.28)] before:pointer-events-none'
                      : theme === 'dark'
                        ? 'border-white/10 bg-gradient-to-b from-slate-900/90 to-slate-950/90 shadow-2xl shadow-black/40'
                        : 'border-slate-200 bg-white shadow-xl shadow-slate-200/40'
                  }`}
                >
                  <div className="min-h-[92px]">
                    <div>
                      <h3 className={`text-2xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                      <p className={`mt-2 text-sm min-h-[48px] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{plan.desc}</p>
                    </div>
                  </div>

                  <div className="mt-6 min-h-[88px]">
                    <div className={`mt-2 text-2xl font-black sm:text-3xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                      {plan.price ? formatEuro(plan.price) : plan.priceLabel}
                      {plan.price && (
                        <span className={`text-base font-normal ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{t.pricingPerMonth}</span>
                      )}
                    </div>
                    <span className={`mt-2 block text-[10px] ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                      {plan.badge}
                    </span>
                  </div>

                  <div className="mt-2 min-h-[76px] pt-4">
                    {authUser && plan.planId && userPlanId === plan.planId ? (
                      <button
                        type="button"
                        aria-disabled="true"
                        onClick={(event) => event.preventDefault()}
                        className={`w-full rounded-full py-2 text-sm font-normal transition opacity-70 ${
                          theme === 'dark'
                            ? 'bg-white/70 text-slate-900'
                            : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        {currentPlanLabel}
                      </button>
                    ) : plan.ctaHref ? (
                      <button
                        type="button"
                        onClick={() => onPricingCta(plan.ctaHref, plan.planId)}
                        className={`w-full rounded-full py-2 text-sm font-normal transition ${
                          plan.highlight
                            ? 'bg-[#038759] text-white ring-1 ring-emerald-300/60 shadow-[0_0_16px_rgba(16,185,129,0.35)]'
                            : theme === 'dark'
                              ? 'bg-white text-slate-900'
                              : 'bg-slate-900 text-white'
                        }`}
                      >
                        {plan.cta}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`w-full rounded-full py-2 text-sm font-normal transition ${
                          theme === 'dark' ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'
                        }`}
                      >
                        {plan.cta}
                      </button>
                    )}
                  </div>

                  <div className={`mt-4 border-t pt-5 flex-1 ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-slate-200' : 'text-slate-700'}`}>
                      {plan.includesLabel}
                    </p>
                    <ul className="mt-4 space-y-3">
                      {plan.features.map((feature: string) => (
                        <li
                          key={feature}
                          className={`flex items-start gap-3 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                        >
                          <span className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${theme === 'dark' ? 'bg-slate-300' : 'bg-slate-500'}`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                </div>
              </Reveal>
            ))}
          </div>

          <p className={`mt-6 text-center text-[10px] px-12 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>{t.pricingFootnote}</p>
        </section>

        <section className="w-full mt-32">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <h2 className={`text-3xl md:text-4xl font-black ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.faqTitle}</h2>
            </div>
          </Reveal>
          <div className="mt-10 space-y-4">
            {t.faqItems.map((item: any, index: number) => {
              const isOpen = openFaqIndexes.has(index);
              return (
                <Reveal key={item.q} delay={index * 120}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenFaqIndexes((prev) => {
                        const next = new Set(prev);
                        if (next.has(index)) {
                          next.delete(index);
                        } else {
                          next.add(index);
                        }
                        return next;
                      });
                    }}
                    className={`w-full rounded-2xl border px-6 py-5 text-left transition ${theme === 'dark' ? 'border-white/10 bg-slate-900/60 hover:border-white/20' : 'border-slate-200 bg-white hover:border-slate-300 shadow-md shadow-slate-200/40'}`}
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h3 className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{item.q}</h3>
                      <div className="flex items-center gap-3">
                        {item.comingSoon && (
                          <span className="px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-amber-500/20 text-amber-300 border border-amber-500/40">
                            {t.faqTagComingSoon}
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition ${isOpen ? 'rotate-180' : ''} ${theme === 'dark' ? 'text-slate-300' : 'text-slate-500'}`} />
                      </div>
                    </div>
                    {isOpen && (
                      <p className={`mt-3 text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{item.a}</p>
                    )}
                  </button>
                </Reveal>
              );
            })}
          </div>
        </section>

        <section className="w-full mt-24 mb-16">
          <Reveal>
            <div className={`rounded-[2rem] border px-6 py-10 md:px-10 md:py-12 text-center ${theme === 'dark' ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white shadow-xl shadow-slate-200/40'}`}>
              <h3 className={`text-2xl md:text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{t.contactCtaTitle}</h3>
              <p className={`mt-3 text-sm md:text-base ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>{t.contactCtaSubtitle}</p>
              <a
                href="mailto:victorsaizalfageme@gmail.com"
                className="mt-6 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-normal bg-[#038759] text-white hover:bg-[#026e49]"
              >
                {t.contactCtaButton}
              </a>
            </div>
          </Reveal>
        </section>
      </div>
    </div>
  );
};

export default App;
